import { motion } from "framer-motion";
import {
  Route, PhoneCall, Luggage, Map, Gauge, BellRing, CloudSun,
  Building2, Share2, Check, Crown,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBanner from "@/components/CtaBanner";
import { MaskedLine } from "@/components/MaskedLine";

const FEATURES = [
  {
    icon: Route, tier: "COACH + FIRST CLASS", title: "Tripsee Timeline",
    desc: "A full, minute-by-minute breakdown from your starting point to the boarding gate: transit, airport entry, security, the walk, boarding. You can literally see where every minute of your trip goes.",
    img: "https://images.unsplash.com/photo-1542296332-2e4473faf563?q=80&w=1200&auto=format&fit=crop",
    alt: "Departures board inside a modern airport terminal",
  },
  {
    icon: PhoneCall, tier: "COACH + FIRST CLASS", title: "Flight Assistance",
    desc: "Quick access to airline contact info and policies, plus helpful guidance when things don't go as planned, right when you need it most.",
    img: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=1200&auto=format&fit=crop",
    alt: "Passport and boarding pass ready for a flight",
  },
  {
    icon: Luggage, tier: "COACH + FIRST CLASS", title: "Baggage Buddy",
    desc: "A customizable packing list with categories, per-item priorities, and packed-vs-not tracking. First Class adds AI Smart Suggestions tuned to your trip type: business, leisure, family, or adventure.",
    img: "https://images.unsplash.com/photo-1569154941061-e231b4725ef1?q=80&w=1200&auto=format&fit=crop",
    alt: "Luggage moving through an airport",
  },
  {
    icon: Map, tier: "FIRST CLASS", title: "Gate Guide",
    desc: "A detailed guide for your exact airport and concourse: baggage-check tips, weather implications, terminal layout, food options, the cleanest bathrooms, airline lounges, and charging spots.",
    img: "https://images.unsplash.com/photo-1519074069444-1ba4eff16b60?q=80&w=1200&auto=format&fit=crop",
    alt: "View from an airplane window above the clouds",
  },
  {
    icon: Gauge, tier: "FIRST CLASS", title: "Flight Reliability",
    desc: "Performance insights for your exact flight number: delay-risk score, on-time percentage, average delay duration, and cancellation rate, so you know what you're walking into before you leave.",
    img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=1200&auto=format&fit=crop",
    alt: "Airplane wing over a sunset horizon",
  },
];

const EXTRAS = [
  { icon: BellRing, title: "Real-time alerts" },
  { icon: CloudSun, title: "Weather insights" },
  { icon: Building2, title: "Airport information" },
  { icon: Route, title: "Flight information" },
  { icon: Share2, title: "Shareable trip summaries" },
];

const TIERS = [
  {
    name: "Coach", tagline: "The essentials, free", featured: false,
    points: ["Tripsee Timeline: 6 per month", "Flight Assistance", "Baggage Buddy", "Real-time alerts & weather"],
  },
  {
    name: "First Class", tagline: "The full instrument panel", featured: true,
    points: ["Tripsee Timeline: 30 per month", "Gate Guide for your exact concourse", "Flight Reliability scores", "AI Smart Suggestions in Baggage Buddy"],
  },
];

export default function Features() {
  return (
    <main>
      <Seo
        title="Features — Tripsee"
        description="Tripsee Timeline, Gate Guide, Flight Reliability, Baggage Buddy and more: the full door-to-gate feature set for stress-free airport arrivals."
      />

      <section className="relative pt-40 pb-24 overflow-hidden bg-radar-grid">
        <div className="absolute inset-0 ambient-glow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-6">Features</p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white max-w-4xl">
            <MaskedLine delay={0.1}>Arrive on time,</MaskedLine>
            <MaskedLine delay={0.26} className="text-primary text-glow">every time.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-7 text-lg sm:text-xl text-slate-400 leading-relaxed max-w-2xl"
          >
            Coach covers the essentials. First Class unlocks the full power of real-time, AI-driven
            trip perfection: detailed flight and airport insight for a stress-free start to every trip.
          </motion.p>
        </div>
      </section>

      {/* APP STORE GRAPHIC */}
      <section className="pt-4 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div
              data-testid="appstore-banner"
              className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/60"
            >
              <img
                src="/img/appstore-banner.webp"
                alt="Tripsee App Store feature graphics — plan door-to-gate, stay on track, monitor flights with live updates, and pack smarter"
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TIERS */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <div
                data-testid={`tier-card-${t.name.toLowerCase().replace(" ", "-")}`}
                className={`h-full rounded-2xl p-9 ${t.featured ? "glass-panel glow-border relative" : "glass-panel"}`}
              >
                {t.featured && (
                  <span className="absolute -top-3.5 left-9 inline-flex items-center gap-1.5 rounded-full bg-[#FFB100] px-4 py-1.5 font-mono text-[11px] font-bold tracking-widest text-[#0B0B0D]">
                    <Crown size={12} strokeWidth={2.5} /> FIRST CLASS
                  </span>
                )}
                <h3 className="font-display text-3xl font-bold text-white">{t.name}</h3>
                <p className="mt-2 text-sm text-slate-400">{t.tagline}</p>
                <ul className="mt-7 space-y-3.5">
                  {t.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                      <Check size={16} className={`mt-0.5 shrink-0 ${t.featured ? "text-[#FFB100]" : "text-primary"}`} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURE DETAILS */}
      <section className="py-16 space-y-24">
        {FEATURES.map((f, i) => (
          <div key={f.title} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <Reveal>
                <div className="overflow-hidden rounded-2xl border border-white/10">
                  <img
                    src={f.img}
                    alt={f.alt}
                    loading="lazy"
                    className="w-full h-72 sm:h-96 object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <span className="font-mono text-[11px] tracking-[0.25em] text-primary border border-primary/30 rounded-full px-4 py-1.5">
                  {f.tier}
                </span>
                <div className="mt-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center text-primary shrink-0">
                    <f.icon size={22} />
                  </div>
                  <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">{f.title}</h2>
                </div>
                <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">{f.desc}</p>
              </Reveal>
            </div>
          </div>
        ))}
      </section>

      {/* EXTRAS */}
      <section className="py-24 bg-[#101315] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-5">Always Included</p>
            <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
              Plus the extras that make it effortless.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {EXTRAS.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.06}>
                <div className="glass-panel rounded-2xl p-6 text-center hover:border-primary/30 transition-colors duration-300">
                  <e.icon size={22} className="mx-auto text-primary" />
                  <p className="mt-4 text-sm font-semibold text-slate-200">{e.title}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner title="Your gate. On time. Every time." />
    </main>
  );
}
